/**
 * 릴리즈 판정 기준 설정 모달 (Max 플랜 전용)
 * 
 * Go/Conditional/Holding/No-Go 기준을 커스터마이즈할 수 있습니다.
 * - 점수 임계값 설정 (슬라이더)
 * - 블로커 규칙 활성화/비활성화 (체크박스)
 * - 커스텀 블로커 추가
 * - 추가 규칙 설정
 * - 도메인 선택 시 AI 추천
 */

import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../i18n';
import {
  getReleaseCriteria,
  saveReleaseCriteria,
  deleteReleaseCriteria,
  getDomainRecommendation,
  type ReleaseCriteria,
  type ReleaseCriteriaInput,
  type DomainType,
  type BlockerType,
  type CustomBlocker,
  type AdditionalRules,
  type DomainRecommendation,
} from '../api';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void;
}

// 기본값
const DEFAULT_CRITERIA: ReleaseCriteriaInput = {
  domain: undefined,
  go_threshold: 70,
  conditional_threshold: 50,
  holding_threshold: 30,
  enabled_blockers: [],
  custom_blockers: [],
  additional_rules: {
    test_fail_downgrades_go: true,
    nfr_missing_forces_holding: false,
    ac_zero_forces_nogo: false,
  },
};

const DOMAINS: DomainType[] = [
  'fintech',
  'ecommerce',
  'healthcare',
  'social',
  'saas',
  'gaming',
  'media',
  'education',
  'other',
];

const BLOCKERS: BlockerType[] = [
  'payment',
  'personal_info',
  'auth',
  'webhook',
  'delete',
];

export default function CriteriaSettingsModal({ isOpen, onClose, onSaveSuccess }: Props) {
  const { t, lang } = useLanguage();
  
  // 토스트 상태
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  // 상태
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // 기존 설정
  const [existingCriteria, setExistingCriteria] = useState<ReleaseCriteria | null>(null);
  
  // 폼 상태
  const [domain, setDomain] = useState<DomainType | undefined>(undefined);
  const [goThreshold, setGoThreshold] = useState(70);
  const [conditionalThreshold, setConditionalThreshold] = useState(50);
  const [holdingThreshold, setHoldingThreshold] = useState(30);
  const [enabledBlockers, setEnabledBlockers] = useState<BlockerType[]>([]);
  const [customBlockers, setCustomBlockers] = useState<CustomBlocker[]>([]);
  const [additionalRules, setAdditionalRules] = useState<AdditionalRules>({
    test_fail_downgrades_go: true,
    nfr_missing_forces_holding: false,
    ac_zero_forces_nogo: false,
  });
  
  // 커스텀 블로커 입력
  const [newBlockerKeyword, setNewBlockerKeyword] = useState('');
  const [newBlockerMessage, setNewBlockerMessage] = useState('');
  
  // AI 추천
  const [recommendation, setRecommendation] = useState<DomainRecommendation | null>(null);
  const [isLoadingRecommendation, setIsLoadingRecommendation] = useState(false);
  const [showRecommendationModal, setShowRecommendationModal] = useState(false);
  const [isRecommendationApplied, setIsRecommendationApplied] = useState(false);
  
  // 데이터 로드
  const loadCriteria = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getReleaseCriteria();
      
      if (data) {
        setExistingCriteria(data);
        setDomain(data.domain ?? undefined);
        setGoThreshold(data.go_threshold);
        setConditionalThreshold(data.conditional_threshold);
        setHoldingThreshold(data.holding_threshold);
        setEnabledBlockers(data.enabled_blockers);
        setCustomBlockers(data.custom_blockers);
        setAdditionalRules(data.additional_rules);
      } else {
        // 기본값 설정
        setDomain(undefined);
        setGoThreshold(DEFAULT_CRITERIA.go_threshold);
        setConditionalThreshold(DEFAULT_CRITERIA.conditional_threshold);
        setHoldingThreshold(DEFAULT_CRITERIA.holding_threshold);
        setEnabledBlockers([]);
        setCustomBlockers([]);
        setAdditionalRules(DEFAULT_CRITERIA.additional_rules);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load criteria');
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  useEffect(() => {
    if (isOpen) {
      loadCriteria();
      setSuccessMessage(null);
      setRecommendation(null);
    }
  }, [isOpen, loadCriteria]);
  
  // 도메인 변경 시 추천 초기화
  useEffect(() => {
    setRecommendation(null);
    setIsRecommendationApplied(false);
  }, [domain]);
  
  // AI 추천 받기
  const handleGetRecommendation = async () => {
    if (!domain) return;
    
    try {
      setIsLoadingRecommendation(true);
      setError(null);
      const rec = await getDomainRecommendation(domain);
      setRecommendation(rec);
      setShowRecommendationModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get recommendation');
    } finally {
      setIsLoadingRecommendation(false);
    }
  };
  
  // 추천 적용
  const handleApplyRecommendation = () => {
    if (!recommendation) return;
    
    setGoThreshold(recommendation.recommended_go_threshold);
    setEnabledBlockers(recommendation.recommended_blockers);
    setAdditionalRules(recommendation.recommended_rules);
    setSuccessMessage(null);
    setShowRecommendationModal(false);
    setIsRecommendationApplied(true);
  };
  
  // 추천 취소
  const handleCancelRecommendation = () => {
    setShowRecommendationModal(false);
  };
  
  // 블로커 토글
  const toggleBlocker = (blocker: BlockerType) => {
    setEnabledBlockers(prev =>
      prev.includes(blocker)
        ? prev.filter(b => b !== blocker)
        : [...prev, blocker]
    );
  };
  
  // 커스텀 블로커 추가
  const addCustomBlocker = () => {
    if (!newBlockerKeyword.trim() || !newBlockerMessage.trim()) return;
    
    setCustomBlockers(prev => [
      ...prev,
      { keyword: newBlockerKeyword.trim(), message: newBlockerMessage.trim() },
    ]);
    setNewBlockerKeyword('');
    setNewBlockerMessage('');
  };
  
  // 커스텀 블로커 삭제
  const removeCustomBlocker = (index: number) => {
    setCustomBlockers(prev => prev.filter((_, i) => i !== index));
  };
  
  // 토스트 표시 함수
  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };
  
  // 저장
  const handleSave = async () => {
    // 유효성 검사
    if (!(goThreshold > conditionalThreshold && conditionalThreshold > holdingThreshold)) {
      setError(t.criteria.validationError);
      return;
    }
    
    try {
      setIsSaving(true);
      setError(null);
      
      const input: ReleaseCriteriaInput = {
        domain,
        go_threshold: goThreshold,
        conditional_threshold: conditionalThreshold,
        holding_threshold: holdingThreshold,
        enabled_blockers: enabledBlockers,
        custom_blockers: customBlockers,
        additional_rules: additionalRules,
      };
      
      await saveReleaseCriteria(input);
      
      // 모달 닫기
      onClose();
      
      // 토스트 메시지 표시 (모달 닫은 후)
      showToastMessage(t.criteria.saveSuccess);
      
      // 콜백 호출
      onSaveSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save criteria');
    } finally {
      setIsSaving(false);
    }
  };
  
  // 초기화
  const handleReset = async () => {
    if (!confirm(t.criteria.resetConfirm)) return;
    
    try {
      setIsSaving(true);
      setError(null);
      
      if (existingCriteria) {
        await deleteReleaseCriteria();
      }
      
      // 기본값으로 설정
      setDomain(undefined);
      setGoThreshold(DEFAULT_CRITERIA.go_threshold);
      setConditionalThreshold(DEFAULT_CRITERIA.conditional_threshold);
      setHoldingThreshold(DEFAULT_CRITERIA.holding_threshold);
      setEnabledBlockers([]);
      setCustomBlockers([]);
      setAdditionalRules(DEFAULT_CRITERIA.additional_rules);
      setExistingCriteria(null);
      setRecommendation(null);
      
      setSuccessMessage(t.criteria.resetSuccess);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      // 404는 무시 (이미 삭제된 경우)
      if (!(err instanceof Error && err.message.includes('설정된 기준이 없습니다'))) {
        setError(err instanceof Error ? err.message : 'Failed to reset criteria');
      }
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <>
      {/* 토스트 메시지 (모달 외부) */}
      {showToast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="flex items-center gap-2 px-4 py-3 bg-green-500 text-white rounded-lg shadow-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium">{toastMessage}</span>
          </div>
        </div>
      )}
      
      {/* AI 추천 팝업 모달 */}
      {showRecommendationModal && recommendation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-[#1a1a2e] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            {/* 헤더 */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <span className="text-2xl">🤖</span>
                {lang === 'ko' ? 'AI 추천 설정' : 'AI Recommended Settings'}
              </h3>
            </div>
            
            {/* 콘텐츠 */}
            <div className="px-6 py-5">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                {lang === 'ko' 
                  ? `선택하신 "${t.criteria.domains[domain!]}" 도메인에 맞도록 AI가 최적의 릴리즈 판정 기준을 설정해드립니다.`
                  : `AI will configure optimal release criteria settings for your "${t.criteria.domains[domain!]}" domain.`
                }
              </p>
              
              {/* 추천 사유 */}
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  <span className="font-semibold">💡 {t.criteria.recommendationReason}:</span>{' '}
                  {recommendation.reason}
                </p>
              </div>
              
              {/* 추천 내용 미리보기 */}
              <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">{t.criteria.goThreshold}</span>
                  <span className="font-medium text-green-600 dark:text-green-400">{recommendation.recommended_go_threshold}점</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">{t.criteria.blockerRules}</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {recommendation.recommended_blockers.length > 0 
                      ? recommendation.recommended_blockers.map(b => t.criteria.blockers[b]).join(', ')
                      : (lang === 'ko' ? '없음' : 'None')
                    }
                  </span>
                </div>
              </div>
            </div>
            
            {/* 푸터 */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <button
                onClick={handleCancelRecommendation}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {lang === 'ko' ? '취소' : 'Cancel'}
              </button>
              <button
                onClick={handleApplyRecommendation}
                className="px-5 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {lang === 'ko' ? '적용' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 모달 */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 sm:p-6">
      <div className="bg-white dark:bg-[#1a1a2e] rounded-2xl shadow-2xl w-full max-w-3xl max-h-full flex flex-col overflow-hidden">
        {/* 헤더 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="text-2xl">⚙️</span>
              {t.criteria.title}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t.criteria.description}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* 콘텐츠 */}
        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <>
              {/* 에러/성공 메시지 */}
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                  {error}
                </div>
              )}
              {successMessage && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 text-sm">
                  {successMessage}
                </div>
              )}
              
              {/* 도메인 선택 */}
              <section>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <span>🏢</span>
                  {t.criteria.domainSelection}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  {t.criteria.domainDescription}
                </p>
                
                <div className="flex flex-wrap gap-2">
                  {DOMAINS.map(d => (
                    <button
                      key={d}
                      onClick={() => setDomain(d === domain ? undefined : d)}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                        domain === d
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-blue-400'
                      }`}
                    >
                      {t.criteria.domains[d]}
                    </button>
                  ))}
                </div>
                
                {domain && (
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      onClick={handleGetRecommendation}
                      disabled={isLoadingRecommendation}
                      className="px-3 py-1.5 text-sm bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                    >
                      {isLoadingRecommendation ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <span>🤖</span>
                      )}
                      {t.criteria.getRecommendation}
                    </button>
                    
                    {isRecommendationApplied && (
                      <span className="text-sm text-green-500 font-medium flex items-center gap-1">
                        ✓ {t.criteria.applyRecommendation}
                      </span>
                    )}
                  </div>
                )}
                
                {isRecommendationApplied && recommendation && (
                  <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                    <p className="text-sm text-purple-700 dark:text-purple-300">
                      <span className="font-semibold">💡 {t.criteria.recommendationReason}:</span>{' '}
                      {recommendation.reason}
                    </p>
                  </div>
                )}
              </section>
              
              {/* 점수 임계값 */}
              <section>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <span>📊</span>
                  {t.criteria.scoreThresholds}
                </h3>
                
                {/* 색상 범위 바 시각화 */}
                <div className="mb-6">
                  <div className="relative h-8 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                    {/* No-Go 영역 (0 ~ Holding) - 빨간색 */}
                    <div 
                      className="absolute top-0 left-0 h-full bg-gradient-to-r from-red-600 to-red-500 flex items-center justify-center"
                      style={{ width: `${holdingThreshold}%` }}
                    >
                      {holdingThreshold >= 15 && (
                        <span className="text-[10px] font-bold text-white drop-shadow-sm">
                          No-Go
                        </span>
                      )}
                    </div>
                    
                    {/* Holding 영역 (Holding ~ Conditional) - 주황색 */}
                    <div 
                      className="absolute top-0 h-full bg-gradient-to-r from-orange-500 to-orange-400 flex items-center justify-center"
                      style={{ 
                        left: `${holdingThreshold}%`, 
                        width: `${conditionalThreshold - holdingThreshold}%` 
                      }}
                    >
                      {conditionalThreshold - holdingThreshold >= 15 && (
                        <span className="text-[10px] font-bold text-white drop-shadow-sm">
                          Holding
                        </span>
                      )}
                    </div>
                    
                    {/* Conditional 영역 (Conditional ~ GO) - 노란색 */}
                    <div 
                      className="absolute top-0 h-full bg-gradient-to-r from-yellow-500 to-yellow-400 flex items-center justify-center"
                      style={{ 
                        left: `${conditionalThreshold}%`, 
                        width: `${goThreshold - conditionalThreshold}%` 
                      }}
                    >
                      {goThreshold - conditionalThreshold >= 15 && (
                        <span className="text-[10px] font-bold text-gray-800 drop-shadow-sm">
                          Conditional
                        </span>
                      )}
                    </div>
                    
                    {/* GO 영역 (GO ~ 100) - 녹색 */}
                    <div 
                      className="absolute top-0 h-full bg-gradient-to-r from-green-500 to-green-400 flex items-center justify-center"
                      style={{ 
                        left: `${goThreshold}%`, 
                        width: `${100 - goThreshold}%` 
                      }}
                    >
                      {100 - goThreshold >= 10 && (
                        <span className="text-[10px] font-bold text-white drop-shadow-sm">
                          GO
                        </span>
                      )}
                    </div>
                    
                    {/* 마커 라인 */}
                    <div 
                      className="absolute top-0 h-full w-0.5 bg-white/50"
                      style={{ left: `${holdingThreshold}%` }}
                    />
                    <div 
                      className="absolute top-0 h-full w-0.5 bg-white/50"
                      style={{ left: `${conditionalThreshold}%` }}
                    />
                    <div 
                      className="absolute top-0 h-full w-0.5 bg-white/50"
                      style={{ left: `${goThreshold}%` }}
                    />
                  </div>
                  
                  {/* 점수 눈금 */}
                  <div className="flex justify-between mt-1 text-[10px] text-gray-500 dark:text-gray-400">
                    <span>0</span>
                    <span>25</span>
                    <span>50</span>
                    <span>75</span>
                    <span>100</span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* GO 점수 */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-green-500"></span>
                        {t.criteria.goThreshold}
                        <span className="text-xs text-gray-400">({goThreshold}~100)</span>
                      </label>
                      <input
                        type="number"
                        min={conditionalThreshold + 1}
                        max={100}
                        value={goThreshold}
                        onChange={e => {
                          const val = Math.max(conditionalThreshold + 1, Math.min(100, Number(e.target.value)));
                          setGoThreshold(val);
                        }}
                        className="w-16 px-2 py-1 text-sm text-center font-bold text-green-500 bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded"
                      />
                    </div>
                    <input
                      type="range"
                      min={conditionalThreshold + 1}
                      max={100}
                      value={goThreshold}
                      onChange={e => setGoThreshold(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-500"
                    />
                  </div>
                  
                  {/* Conditional 점수 */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                        {t.criteria.conditionalThreshold}
                        <span className="text-xs text-gray-400">({conditionalThreshold}~{goThreshold - 1})</span>
                      </label>
                      <input
                        type="number"
                        min={holdingThreshold + 1}
                        max={goThreshold - 1}
                        value={conditionalThreshold}
                        onChange={e => {
                          const val = Math.max(holdingThreshold + 1, Math.min(goThreshold - 1, Number(e.target.value)));
                          setConditionalThreshold(val);
                        }}
                        className="w-16 px-2 py-1 text-sm text-center font-bold text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded"
                      />
                    </div>
                    <input
                      type="range"
                      min={holdingThreshold + 1}
                      max={goThreshold - 1}
                      value={conditionalThreshold}
                      onChange={e => setConditionalThreshold(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-yellow-500"
                    />
                  </div>
                  
                  {/* Holding 점수 */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                        {t.criteria.holdingThreshold}
                        <span className="text-xs text-gray-400">({holdingThreshold}~{conditionalThreshold - 1})</span>
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={conditionalThreshold - 1}
                        value={holdingThreshold}
                        onChange={e => {
                          const val = Math.max(1, Math.min(conditionalThreshold - 1, Number(e.target.value)));
                          setHoldingThreshold(val);
                        }}
                        className="w-16 px-2 py-1 text-sm text-center font-bold text-orange-500 bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700 rounded"
                      />
                    </div>
                    <input
                      type="range"
                      min={1}
                      max={conditionalThreshold - 1}
                      value={holdingThreshold}
                      onChange={e => setHoldingThreshold(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-orange-500"
                    />
                  </div>
                  
                  {/* No-Go 설명 */}
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 p-2 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-200 dark:border-red-800">
                    <span className="w-3 h-3 rounded-full bg-red-500"></span>
                    <span className="text-red-600 dark:text-red-400 font-medium">No-Go</span>
                    <span className="text-xs">(0~{holdingThreshold - 1})</span>
                    <span className="mx-1">—</span>
                    <span className="text-xs">{t.criteria.belowHolding}</span>
                  </div>
                </div>
              </section>
              
              {/* 블로커 규칙 */}
              <section>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <span>🚫</span>
                  {t.criteria.blockerRules}
                </h3>
                
                {/* 블로커 설명 박스 */}
                <div className="p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg mb-4">
                  <div className="flex items-start gap-2">
                    <span className="text-lg">💡</span>
                    <div className="text-sm text-amber-800 dark:text-amber-200">
                      <p className="font-medium mb-1">
                        {lang === 'ko' ? '블로커란?' : 'What is a Blocker?'}
                      </p>
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        {lang === 'ko' 
                          ? '아래 항목을 체크하면, 기획서에 해당 키워드가 있을 때 반드시 예외/오류 처리 방안이 함께 기술되어 있어야 합니다. 예외 처리가 없으면 점수와 관계없이 No-Go 처리됩니다.'
                          : 'When checked, if the planning document contains these keywords, exception/error handling must also be documented. Without it, the result will be No-Go regardless of score.'
                        }
                      </p>
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                        {lang === 'ko'
                          ? '예) "결제" 체크 시 → 기획서에 "결제" 언급이 있으면 "결제 실패", "환불", "중복 결제" 등의 처리 방안도 있어야 함'
                          : 'Ex) If "Payment" is checked → If "payment" is mentioned, handling for "payment failure", "refund", etc. must also be present'
                        }
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {BLOCKERS.map(blocker => (
                    <label
                      key={blocker}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                        enabledBlockers.includes(blocker)
                          ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={enabledBlockers.includes(blocker)}
                        onChange={() => toggleBlocker(blocker)}
                        className="w-4 h-4 text-red-500 rounded focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {t.criteria.blockers[blocker]}
                      </span>
                    </label>
                  ))}
                </div>
              </section>
              
              {/* 커스텀 블로커 */}
              <section>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <span>➕</span>
                  {t.criteria.customBlockers}
                </h3>
                
                {/* 기존 커스텀 블로커 목록 */}
                {customBlockers.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {customBlockers.map((blocker, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {blocker.keyword}
                        </span>
                        <span className="text-gray-400">→</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400 flex-1">
                          {blocker.message}
                        </span>
                        <button
                          onClick={() => removeCustomBlocker(index)}
                          className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* 새 커스텀 블로커 추가 */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newBlockerKeyword}
                    onChange={e => setNewBlockerKeyword(e.target.value)}
                    placeholder={t.criteria.keywordPlaceholder}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400"
                  />
                  <input
                    type="text"
                    value={newBlockerMessage}
                    onChange={e => setNewBlockerMessage(e.target.value)}
                    placeholder={t.criteria.messagePlaceholder}
                    className="flex-[2] px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400"
                  />
                  <button
                    onClick={addCustomBlocker}
                    disabled={!newBlockerKeyword.trim() || !newBlockerMessage.trim()}
                    className="px-3 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t.criteria.addCustomBlocker}
                  </button>
                </div>
              </section>
              
              {/* 추가 규칙 */}
              <section>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <span>⚡</span>
                  {t.criteria.additionalRules}
                </h3>
                
                {/* 추가 규칙 설명 박스 */}
                <div className="p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg mb-4">
                  <div className="flex items-start gap-2">
                    <span className="text-lg">💡</span>
                    <div className="text-sm text-blue-800 dark:text-blue-200">
                      <p className="font-medium mb-1">
                        {lang === 'ko' ? '추가 규칙이란?' : 'What are Additional Rules?'}
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        {lang === 'ko' 
                          ? '점수 외에 특정 조건이 충족되면 결과를 자동으로 변경하는 규칙입니다. 체크하면 해당 조건이 적용됩니다.'
                          : 'Rules that automatically change the result when specific conditions are met, regardless of score. Check to enable.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {/* 테스트 실패 시 다운그레이드 */}
                  <label className="flex items-start gap-3 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750">
                    <input
                      type="checkbox"
                      checked={additionalRules.test_fail_downgrades_go}
                      onChange={e => setAdditionalRules(prev => ({
                        ...prev,
                        test_fail_downgrades_go: e.target.checked,
                      }))}
                      className="w-4 h-4 mt-0.5 text-blue-500 rounded focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {lang === 'ko' ? '테스트 실패 시 GO 불가' : 'No GO if Test Fails'}
                        </p>
                        <span className="text-xs px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded">
                          {lang === 'ko' ? '권장' : 'Recommended'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {lang === 'ko'
                          ? 'AI 테스트케이스 중 "실패" 상태가 하나라도 있으면, 점수가 GO 기준을 넘어도 Conditional로 처리'
                          : 'If any AI test case is marked as "Fail", result becomes Conditional even if score meets GO threshold'
                        }
                      </p>
                      <div className="mt-2 flex items-center gap-2 text-xs">
                        <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">GO</span>
                        <span>→</span>
                        <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded">Conditional</span>
                      </div>
                    </div>
                  </label>
                  
                  {/* NFR 누락 시 Holding */}
                  <label className="flex items-start gap-3 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750">
                    <input
                      type="checkbox"
                      checked={additionalRules.nfr_missing_forces_holding}
                      onChange={e => setAdditionalRules(prev => ({
                        ...prev,
                        nfr_missing_forces_holding: e.target.checked,
                      }))}
                      className="w-4 h-4 mt-0.5 text-blue-500 rounded focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {lang === 'ko' ? 'NFR(비기능 요구사항) 없으면 Holding' : 'Holding if NFR Missing'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {lang === 'ko'
                          ? '성능, 보안, 확장성 등 비기능 요구사항이 기획서에 없으면 무조건 Holding 처리'
                          : 'If non-functional requirements (performance, security, scalability) are not documented, result becomes Holding'
                        }
                      </p>
                      <div className="mt-2 flex items-center gap-2 text-xs">
                        <span className="text-gray-500">{lang === 'ko' ? 'NFR 0점' : 'NFR = 0'}</span>
                        <span>→</span>
                        <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded">Holding</span>
                      </div>
                    </div>
                  </label>
                  
                  {/* AC 0점 시 No-Go */}
                  <label className="flex items-start gap-3 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750">
                    <input
                      type="checkbox"
                      checked={additionalRules.ac_zero_forces_nogo}
                      onChange={e => setAdditionalRules(prev => ({
                        ...prev,
                        ac_zero_forces_nogo: e.target.checked,
                      }))}
                      className="w-4 h-4 mt-0.5 text-blue-500 rounded focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {lang === 'ko' ? 'AC(인수 기준) 없으면 No-Go' : 'No-Go if AC Missing'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {lang === 'ko'
                          ? 'Given/When/Then 형식의 인수 기준이 전혀 없으면 무조건 No-Go 처리'
                          : 'If no acceptance criteria (Given/When/Then) is documented, result becomes No-Go'
                        }
                      </p>
                      <div className="mt-2 flex items-center gap-2 text-xs">
                        <span className="text-gray-500">{lang === 'ko' ? 'AC 0점' : 'AC = 0'}</span>
                        <span>→</span>
                        <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded">No-Go</span>
                      </div>
                    </div>
                  </label>
                </div>
              </section>
            </>
          )}
        </div>
        
        {/* 푸터 */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
          <button
            onClick={handleReset}
            disabled={isLoading || isSaving}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50"
          >
            {t.criteria.reset}
          </button>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              {lang === 'ko' ? '닫기' : 'Close'}
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading || isSaving}
              className="px-6 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              {t.criteria.save}
            </button>
          </div>
        </div>
      </div>
    </div>
      )}
    </>
  );
}

